import React, { useState, useEffect, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
	useAuthWithGoogleTokenMutation,
	useAuthWithAppleTokenMutation,
	useRequestOtpMutation,
	useVerifyOtpMutation,
} from '@/store/api';
import { useOtpCooldown, usePostAuthFlow, useIsMobile } from '@/hooks';
import { useToast } from '@/components/ui/use-toast';
import {
	wtnGoogleLogin,
	isRunningInWTN,
	wtnAppleLogin,
	trimInputs,
	extractGoogleTokenPayload,
	buildAuthScreenUrl,
	getErrorMessage,
	asApiError,
	consumeSignUpAfterTradeAttempt,
	getUtmFromStorage,
	getMobileBrowserPlatform,
	isXInAppBrowser as detectXInAppBrowser,
	openCurrentPageInExternalBrowser,
	type MobileBrowserPlatform,
} from '@/utils';
import { logSentry, logSentryError } from '@/sentry';
import { useNavigate, useSearchParams } from 'react-router';
import { AUTH_SCREENS, ROUTES, APPLE_CLIENT_ID } from '@/constants';
import { useThirdPartyAuth } from '@/contexts/third-party.context';
import { isNativeApp } from 'webtonative';
import mixpanel from '@/configs/mixpanel';

interface UseAuthFlowOptions {
	onClose?: () => void;
	redirectUrl?: string | null;
	onSignUpStep?: (email: string) => void;
}

const GOOGLE_AUTH_FALLBACK_MESSAGE = 'Unable to sign in with Google. Please try again.';

const isRawGoogleTransportError = (message: string) => {
	const normalized = message.toLowerCase();

	return (
		normalized.includes('oauth2.googleapis.com') ||
		normalized.includes('tokeninfo') ||
		normalized.includes('premature close') ||
		normalized.includes('err_stream_premature_close') ||
		normalized.includes('invalid response body')
	);
};

const getGoogleAuthErrorMessage = (err: unknown) => {
	const message = getErrorMessage(err, GOOGLE_AUTH_FALLBACK_MESSAGE);

	return isRawGoogleTransportError(message) ? GOOGLE_AUTH_FALLBACK_MESSAGE : message;
};

const GOOGLE_X_BROWSER_MESSAGE =
	'Google sign-in cannot open inside X. Open this page in your browser to continue.';

const hasWTNBridge = () => {
	if (typeof window === 'undefined') return false;

	// Check the official WebToNative native bridges first (available immediately at page load)
	if (isNativeApp) return true;

	// Fallback: check window.WTN which may be injected later
	return Boolean(window.WTN);
};

export function useAuthFlow({
	onClose,
	redirectUrl: redirectUrlOption,
	onSignUpStep,
}: UseAuthFlowOptions = {}) {
	const isMobile = useIsMobile();
	const [email, setEmail] = useState('');
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isWTN, setIsWTN] = useState(hasWTNBridge);
	const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
	const [isXInAppBrowser] = useState(detectXInAppBrowser);
	const [xInAppBrowserPlatform] = useState<MobileBrowserPlatform>(getMobileBrowserPlatform);
	const [showXBrowserHandoff, setShowXBrowserHandoff] = useState(false);
	const nativeAuthInFlightRef = useRef(false);

	const { isCooldownActive, startCooldown } = useOtpCooldown(email);

	const [authWithGoogleToken] = useAuthWithGoogleTokenMutation();
	const [authWithAppleToken] = useAuthWithAppleTokenMutation();
	const [requestOtp] = useRequestOtpMutation();
	const [verifyOtp] = useVerifyOtpMutation();

	const { toast } = useToast();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { collectReferralData, handlePostAuth } = usePostAuthFlow();
	const thirdPartyAuth = useThirdPartyAuth();

	const utm = getUtmFromStorage();

	const trackAuthCompletedIfProbability = (
		method: 'email' | 'google' | 'apple',
		isNewRegistration: boolean,
		userEmail?: string,
		username?: string
	) => {
		if (window.location.pathname.includes('probability-event')) {
			const tradeAttempt = consumeSignUpAfterTradeAttempt();
			const authCompletedProps = {
				...tradeAttempt,
				auth_method: method,
				is_new_registration: isNewRegistration,
				...(userEmail ? { userEmail } : {}),
				...(username ? { username } : {}),
			};
			mixpanel.track('[AUTH COMPLETED]', authCompletedProps);
			window.dataLayer?.push({ event: 'auth_completed', ...authCompletedProps });
		}
	};

	const getFallbackRedirectUrl = () => {
		if (typeof window === 'undefined') return null;
		const path = window.location.pathname;
		if (path.startsWith(ROUTES.AUTH) || path === '/') {
			return null;
		}
		return `${path}${window.location.search}${window.location.hash}`;
	};

	const redirectUrl = redirectUrlOption ?? searchParams.get('redirect') ?? getFallbackRedirectUrl();

	const googleWebLogin = useGoogleLogin({
		onSuccess: (tokenResponse) => onGoogleWebAuthResponse(tokenResponse),
		onError: () => setError('Google login failed. Please try again.'),
	});

	const trackXBrowserGoogleAuth = (
		action: 'blocked_attempt' | 'open_browser_clicked' | 'copy_link_clicked'
	) => {
		const props = {
			auth_method: 'google',
			blocked_in_app_browser: true,
			browser_provider: 'x',
			action,
		};
		mixpanel.track('[AUTH ATTEMPTED]', props);
		window.dataLayer?.push({ event: 'auth_attempted', ...props });
	};

	const onGoogleExternalBrowserHandoff = async () => {
		trackXBrowserGoogleAuth('blocked_attempt');
		setError(GOOGLE_X_BROWSER_MESSAGE);
		setShowXBrowserHandoff(true);
		if (xInAppBrowserPlatform === 'android') {
			trackXBrowserGoogleAuth('open_browser_clicked');
			openCurrentPageInExternalBrowser();
		}
	};

	const onCopyExternalBrowserLink = async () => {
		if (typeof window === 'undefined') return;

		try {
			await navigator.clipboard.writeText(window.location.href);
			trackXBrowserGoogleAuth('copy_link_clicked');
			toast({
				variant: 'success',
				title: 'Link copied',
				description: 'Open it in Safari or Chrome to continue with Google.',
			});
		} catch (err) {
			console.error('Failed to copy browser link:', err);
			toast({
				variant: 'destructive',
				title: 'Could not copy link',
				description: 'Use the menu in X and choose Open in browser.',
			});
		}
	};

	useEffect(() => {
		if (isWTN) return;

		let isMounted = true;

		isRunningInWTN({ force: true }).then((detected) => {
			if (isMounted && detected) setIsWTN(true);
		});

		const interval = setInterval(() => {
			if (hasWTNBridge()) {
				if (isMounted) setIsWTN(true);
				clearInterval(interval);
			}
		}, 500);

		return () => {
			isMounted = false;
			clearInterval(interval);
		};
	}, []);

	const close = () => {
		onClose?.();
		setIsVerifyingOtp(false);
	};

	const onGoogleMobileAuth = async () => {
		// A second tap while the native flow is open would re-register the native
		// callback hook and orphan the first attempt, so ignore concurrent calls.
		if (nativeAuthInFlightRef.current) return;
		nativeAuthInFlightRef.current = true;
		// Keep the button in a loading state for the entire native flow so it
		// doesn't look tappable-but-dead while the in-flight guard is active.
		setIsLoading(true);
		try {
			await runGoogleMobileAuth();
		} finally {
			nativeAuthInFlightRef.current = false;
			setIsLoading(false);
		}
	};

	const runGoogleMobileAuth = async () => {
		logSentry('GoogleMobile: handler called', { envWTN: !!window.WTN });
		const result = await wtnGoogleLogin();
		const googleTokenPayload = extractGoogleTokenPayload(result);

		const isSuccess = result?.isSuccess !== false && !result?.error;

		if (isSuccess && googleTokenPayload) {
			try {
				const { referralCode, fingerprint, ipAddress, data } = await collectReferralData();
				const apiRes = await authWithGoogleToken(googleTokenPayload).unwrap();

				if (apiRes.needsBirthdate) {
					if (!isMobile && onSignUpStep) {
						onSignUpStep(apiRes.googleEmail);
						return;
					}
					close();
					thirdPartyAuth.onSuccess('google', () => {
						navigate(
							buildAuthScreenUrl({
								screen: AUTH_SCREENS.SIGNUP,
								email: apiRes.googleEmail,
								name: apiRes.name,
								redirect: redirectUrl,
							})
						);
					});
					return;
				}

				close();
				const continueAuth = () =>
					handlePostAuth({
						...apiRes,
						referralCode,
						data,
						fingerprint,
						ipAddress,
						redirectUrl,
					});

				trackAuthCompletedIfProbability(
					'google',
					!!apiRes.isNewUser,
					apiRes.googleEmail,
					apiRes.name
				);

				await continueAuth();
			} catch (err) {
				logSentryError(err, { where: 'authWithGoogleToken', stage: 'post-login' });
				thirdPartyAuth.onError(getGoogleAuthErrorMessage(err));
			}
			return;
		}

		logSentry('GoogleMobile: login did not return token', {
			type: result?.type,
			error: result?.error,
			isSuccess: result?.isSuccess,
			hasToken: !!googleTokenPayload,
			resultKeys: Object.keys(result || {}),
			// No token present on this path, so the raw payload is safe to log
			rawResult: typeof result === 'string' ? result : JSON.stringify(result)?.slice(0, 500),
		});
		toast({
			variant: 'destructive',
			title: result?.error ? 'Google login unavailable' : 'Google login cancelled',
			description: result?.error || 'Please try again.',
		});
	};

	const onAppleMobileAuth = async () => {
		if (nativeAuthInFlightRef.current) return;
		nativeAuthInFlightRef.current = true;
		setIsLoading(true);
		try {
			await runAppleMobileAuth();
		} finally {
			nativeAuthInFlightRef.current = false;
			setIsLoading(false);
		}
	};

	const runAppleMobileAuth = async () => {
		logSentry('AppleMobile: handler called', { envWTN: !!window.WTN });
		try {
			const result = await wtnAppleLogin();
			const identityToken =
				result?.identityToken ??
				result?.idToken ??
				result?.identity_token ??
				result?.id_token ??
				result?.token;

			const isSuccess = result?.isSuccess !== false && !result?.error;

			if (!isSuccess || !identityToken) {
				logSentry('AppleMobile: login did not return identity token', {
					type: result?.type,
					error: result?.error,
					hasToken: !!identityToken,
					resultKeys: Object.keys(result || {}),
				});
				toast({
					variant: 'destructive',
					title: result?.error ? 'Apple login unavailable' : 'Apple login cancelled',
					description: result?.error || 'Please try again.',
				});
				return;
			}

			const { referralCode, ipAddress, fingerprint, data } = await collectReferralData();

			const payload = {
				identityToken,
				firstName: result?.fullName?.givenName || result?.firstName || 'apple',
				lastName: result?.fullName?.familyName || result?.lastName || 'apple',
				utmSource: utm?.utm_source,
				...(referralCode ? { referralCode, fingerprint, ipAddress } : {}),
			};

			const apiRes = await authWithAppleToken(payload).unwrap();
			close();
			const continueAuth = () =>
				handlePostAuth({
					...apiRes,
					referralCode,
					data,
					fingerprint,
					ipAddress,
					redirectUrl,
				});

			trackAuthCompletedIfProbability('apple', !!apiRes.isNewUser, apiRes.email, apiRes.firstName);
			if (apiRes.isNewUser) {
				thirdPartyAuth.onSuccess('apple', continueAuth);
				return;
			}

			await continueAuth();
		} catch (err) {
			logSentryError(err, { where: 'authWithAppleToken', stage: 'post-login' });
			thirdPartyAuth.onError(
				getErrorMessage(err, 'Unable to sign in with Apple. Please try again.')
			);
		}
	};

	const onGoogleWebAuth = async () => {
		if (isXInAppBrowser) {
			onGoogleExternalBrowserHandoff();
			return;
		}
		googleWebLogin();
	};

	const onGoogleWebAuthResponse = async (response: any) => {
		const googleTokenPayload = response?.credential
			? { idToken: response.credential }
			: response?.access_token
				? { accessToken: response.access_token }
				: null;

		if (googleTokenPayload) {
			try {
				setIsLoading(true);
				const { referralCode, fingerprint, ipAddress, data } = await collectReferralData();
				const res = await authWithGoogleToken(googleTokenPayload).unwrap();

				if (res.needsBirthdate) {
					if (!isMobile && onSignUpStep) {
						onSignUpStep(res.googleEmail);
						return;
					}
					close();
					thirdPartyAuth.onSuccess('google', () => {
						navigate(
							buildAuthScreenUrl({
								screen: AUTH_SCREENS.SIGNUP,
								email: res.googleEmail,
								name: res.name,
								redirect: redirectUrl,
							})
						);
					});
					return;
				}

				close();
				const continueAuth = () =>
					handlePostAuth({
						...res,
						referralCode,
						data,
						fingerprint,
						ipAddress,
						redirectUrl,
					});

				trackAuthCompletedIfProbability('google', !!res.isNewUser, res.googleEmail, res.name);
				if (res.isNewUser) {
					thirdPartyAuth.onSuccess('google', continueAuth);
					return;
				}

				await continueAuth();
			} catch (err) {
				console.error('Google login error:', err);
				thirdPartyAuth.onError(getGoogleAuthErrorMessage(err));
			} finally {
				setIsLoading(false);
			}
		}
	};

	const onAppleWebAuth = async () => {
		const AppleID = (window as any).AppleID;
		if (!AppleID) {
			toast({
				variant: 'destructive',
				title: 'Apple login unavailable',
				description: 'Please try again later.',
			});
			return;
		}

		AppleID.auth.init({
			clientId: APPLE_CLIENT_ID,
			scope: 'name email',
			redirectURI: `${window.location.origin}/auth/apple/callback`,
			usePopup: true,
		});
		const signInPromise = AppleID.auth.signIn();
		setIsLoading(true);

		try {
			const response = await signInPromise;

			const identityToken = response?.authorization?.id_token;
			if (!identityToken) {
				toast({
					variant: 'destructive',
					title: 'Apple login failed',
					description: 'Please try again.',
				});
				return;
			}

			const { referralCode, ipAddress, fingerprint, data } = await collectReferralData();
			const payload = {
				identityToken,
				firstName: response?.user?.name?.firstName || 'apple',
				lastName: response?.user?.name?.lastName || 'apple',
				utmSource: utm?.utm_source,
				...(referralCode ? { referralCode, fingerprint, ipAddress } : {}),
			};

			const apiRes = await authWithAppleToken(payload).unwrap();
			close();
			const continueAuth = () =>
				handlePostAuth({ ...apiRes, referralCode, data, fingerprint, ipAddress, redirectUrl });

			trackAuthCompletedIfProbability('apple', !!apiRes.isNewUser, apiRes.email, apiRes.firstName);
			if (apiRes.isNewUser) {
				thirdPartyAuth.onSuccess('apple', continueAuth);
				return;
			}

			await continueAuth();
		} catch (err: unknown) {
			console.error('Apple login error:', err);
			if (asApiError(err).error === 'popup_closed_by_user') return;
			logSentryError(err, { where: 'authWithAppleToken web', stage: 'post-login' });
			thirdPartyAuth.onError(
				getErrorMessage(err, 'Unable to sign in with Apple. Please try again.')
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRequestOtp = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');

		const { email: trimmedEmail } = trimInputs({ email });
		if (!trimmedEmail) return setError('Email is required');

		const allowedEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		const hasInvalidCharacters = /[^a-zA-Z0-9._%+-@]/.test(trimmedEmail);
		const hasDomain = /@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail);

		if (hasInvalidCharacters && !hasDomain) {
			return setError('Invalid email format. Check for extra symbols and the domain format.');
		}
		if (hasInvalidCharacters) {
			return setError('Invalid character found. Please remove special symbols to continue.');
		}
		if (!hasDomain) {
			return setError('Invalid email domain. Please enter a valid domain (e.g. name@domain.com).');
		}
		if (!allowedEmailPattern.test(trimmedEmail)) {
			return setError('Invalid email format. Please enter a valid email address.');
		}

		const domain = trimmedEmail.split('@')[1];
		if (domain.toLowerCase() === 'gmail.com') {
			const shouldUseWTN = isWTN || (await isRunningInWTN({ force: true }));

			setIsWTN(shouldUseWTN);

			// The native (WTN) flow only works inside the app; mobile browsers must
			// use the web OAuth flow or they'd always fail with "only available in the app".
			if (shouldUseWTN) {
				onGoogleMobileAuth();
			} else if (isXInAppBrowser) {
				onGoogleExternalBrowserHandoff();
			} else {
				onGoogleWebAuth();
			}
			return;
		}

		setIsLoading(true);

		if (isCooldownActive) {
			setIsVerifyingOtp(true);
			setIsLoading(false);
			return;
		}

		try {
			await requestOtp({ email: trimmedEmail }).unwrap();
			startCooldown();
			setIsVerifyingOtp(true);
		} catch (err: unknown) {
			console.error('OTP request error:', err);
			setError(getErrorMessage(err, 'Failed to send OTP. Please try again.'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOtp = async (code: string) => {
		setError('');
		setIsLoading(true);
		try {
			const { referralCode, fingerprint, ipAddress, data } = await collectReferralData();
			const res = await verifyOtp({ email, code }).unwrap();

			if (res.isNewUser) {
				if (!isMobile && onSignUpStep) {
					onSignUpStep(email);
					return;
				}
				onClose?.();
				thirdPartyAuth.onSuccess('email', () => {
					navigate(
						buildAuthScreenUrl({
							screen: AUTH_SCREENS.SIGNUP,
							email,
							redirect: redirectUrl,
						})
					);
				});
				return;
			}

			onClose?.();
			trackAuthCompletedIfProbability('email', false, email);
			await handlePostAuth({
				...res,
				referralCode,
				data,
				fingerprint,
				ipAddress,
				redirectUrl,
			});
		} catch (err: unknown) {
			console.error('OTP verify error:', err);
			setError(getErrorMessage(err, 'Invalid code. Please try again.'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendOtp = async () => {
		setError('');
		setIsLoading(true);
		try {
			await requestOtp({ email }).unwrap();
			toast({
				variant: 'success',
				title: 'Code resent',
				description: 'Please check your email.',
			});
		} catch (err: unknown) {
			console.error('OTP resend error:', err);
			setError(getErrorMessage(err, 'Failed to resend OTP.'));
		} finally {
			setIsLoading(false);
		}
	};

	return {
		email,
		setEmail,
		error,
		setError,
		isLoading,
		isWTN,
		isVerifyingOtp,
		setIsVerifyingOtp,
		handleRequestOtp,
		handleVerifyOtp,
		handleResendOtp,
		onGoogleMobileAuth,
		onAppleMobileAuth,
		onGoogleWebAuth,
		onGoogleExternalBrowserHandoff,
		onCopyExternalBrowserLink,
		onGoogleWebAuthResponse,
		onAppleWebAuth,
		isXInAppBrowser,
		xInAppBrowserPlatform,
		showXBrowserHandoff,
		redirectUrl,
	};
}
